import { Box, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { makeStyles } from 'tss-react/mui'
import { calculateWidgetDistribution } from './utils/widget-distribution.utils'
import { WIDGET_GAP, WIDGET_MIN_WIDTH } from './widgets/base-card/const'

const useStyles = makeStyles<{ isOdd: boolean }>()((theme, { isOdd }) => ({
  responsiveContainerRoot: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  mobileResponsiveContainerRoot: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
      gridAutoRows: '1fr',
      alignItems: 'stretch',
      '& > *': {
        flex: '1 1 100%',
      },
      ...(isOdd && {
        '& > *:last-child': {
          gridColumn: '1 / -1', // Make the last item full width if odd number of widgets
        },
      }),
    },
  },
}))

interface ContainerProps {
  children: React.ReactNode
}

/**
 * A wrapper component to handle the responsive layout of dashboard widgets-
 */
const DashboardWidgetContainer: React.FC<ContainerProps> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const widgets = React.Children.toArray(children)
  const amountOfWidgets = widgets.length
  const isOddWidgetCount = amountOfWidgets % 2 !== 0
  const { classes } = useStyles({ isOdd: isOddWidgetCount })
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [numberOfRows, setNumberOfRows] = React.useState(1)
  const containerWidth = ref?.current?.offsetWidth ?? 0
  const distributedWidgets = calculateWidgetDistribution(amountOfWidgets, numberOfRows, widgets)

  useEffect(() => {
    if (downSm) return
    const maxColumns = Math.floor(containerWidth / (WIDGET_MIN_WIDTH + WIDGET_GAP))
    if (amountOfWidgets > 0 && maxColumns > 0) {
      setNumberOfRows(Math.ceil(amountOfWidgets / maxColumns))
    }
  }, [amountOfWidgets, containerWidth, downSm])

  if (downSm) {
    return <Box className={classes.mobileResponsiveContainerRoot}>{children}</Box>
  }

  return (
    <Box className={classes.responsiveContainerRoot} ref={ref}>
      {distributedWidgets.map((items, rowIndex) => (
        <Box
          key={`row-${rowIndex}`}
          style={{
            display: 'flex',
            gap: WIDGET_GAP,
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {...items}
        </Box>
      ))}
    </Box>
  )
}

export default DashboardWidgetContainer
